import { DamageType } from "../damageType";
import { Direction } from "../direction";
import { BigContainerOpening } from "../effects/bigContainerOpening";
import { Explosion } from "../effects/explosion";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import type { Bumpable } from "../interfaces";
import { Key } from "../items/key";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { Player } from "../player";
import { randomChance } from "../random";
// serialization handled via restoreCoreFields
import type { Thing } from "../thing";
import { XY } from "../xy";
import { BoxFloor } from "./floors/boxFloor";
import { LootBox } from "./lootBox";
import { MultiTileThingController } from "./multitile/multiTileThingController";
import { MultiTileThingCore } from "./multitile/multiTileThingCore";
import { MultiTileThingPart } from "./multitile/multiTileThingPart";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { ZOrder } from "../zOrder";

const PROBABILITY_TO_DESTROY_CONTENT_ON_FORCED_OPEN = 0.7;

export class BigContainer extends MultiTileThingCore implements Bumpable {
	static key = "BigContainer";
	private loot: LootBox[] = [];

	constructor() {
		super(true);
		this.hp = STD_BASE_HP * 3;
		this.maxHp = STD_BASE_HP * 3;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.ELECTRIC, 0);
	}

	static initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const core = new BigContainer();
		core.xy = xy;

		const controller = new MultiTileThingController(core);
		core.controller = controller;
		board.putThing(core);

		const offsets = [new XY(0, 0), new XY(1, 0), new XY(0, 1), new XY(1, 1)];
		const parts: MultiTileThingPart[] = [];
		for (const offset of offsets) {
			const part = new MultiTileThingPart(xy.add(offset));
			part.linkToController(controller);
			controller.getParts().add(part);
			board.putThing(part);
			parts.push(part);
		}

		return [core, ...parts];
	}

	getZOrder(): number {
		return ZOrder.SCENARIO;
	}

	isHideUnderneath(): boolean {
		return false;
	}

	isPassable(): boolean {
		return false;
	}

	addLoot(lootBox: LootBox): void {
		this.loot.push(lootBox);
	}

	getLoot(): LootBox[] {
		return this.loot;
	}

	open(): void {
		if (!this.controller) return;
		for (const part of this.controller.getParts()) {
			this.board!.removeAtEndOfTurn(part);
		}
		this.placeLoot();
		this.createBoxBottom();
		this.board!.addEffect(new BigContainerOpening(this.controller!.getRenderAnchorXY()));
		this.board!.addNonPhysicalThing(new Sound(SoundId.BIG_CONTAINER_OPEN, this.controller!.getRenderAnchorXY()));
		this.board!.removeAtEndOfTurn(this);
	}

	private placeLoot(): void {
		const lootQueue = [...this.loot];
		const partsXYs = this.controller ? [...this.controller.getParts()].map((p) => p.xy) : [];

		while (lootQueue.length > 0 && partsXYs.length > 0) {
			const lootItem = lootQueue.shift()!;
			const xy = partsXYs.shift()!;
			lootItem.xy = xy;
			this.board!.addThing(lootItem);
		}
	}

	private createBoxBottom(): void {
		if (!this.controller) return;
		const root = this.controller.getBottomLeftXY();
		// root is (minX, minY) = screen top-left in TS (no Y-flip).
		// Directions are visual variants: NW=top-left, NE=top-right, SW=bottom-left, SE=bottom-right.
		this.board!.addThing(new BoxFloor(root, Direction.NW));
		this.board!.addThing(new BoxFloor(root.add(1, 0), Direction.NE));
		this.board!.addThing(new BoxFloor(root.add(0, 1), Direction.SW));
		this.board!.addThing(new BoxFloor(root.add(1, 1), Direction.SE));
	}

	protected onZeroHp(cause: Thing | null): void {
		if (this.board) this.board.stats.recordObstacleDestroyed();
		// Force open: destroy 70% of loot randomly, then place remaining
		this.loot = this.loot.filter(() => !randomChance(PROBABILITY_TO_DESTROY_CONTENT_ON_FORCED_OPEN));
		this.placeLoot();
		this.createBoxBottom();
		if (this.controller) {
			for (const part of this.controller.getParts()) {
				this.board!.addEffect(new Explosion(part.xy));
			}
		}
		super.onZeroHp(cause);
	}

	bump(_direction: Direction, author: PhysicalThing): boolean {
		if (!(author instanceof Player)) return false;
		const player = author;

		if (!player.inventory.getItemOfClass(Key)) {
			this.board!.addEvent(new Event(EventType.DO_NOT_HAVE_KEY));
			return false;
		}

		player.inventory.consumeOne(Key);
		this.open();
		this.board!.addEvent(new Event(EventType.CONTAINER_OPENS));
		this.board!.stats.recordContainerOpened();
		return true;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			loot: this.loot.map((l) => l.serialize()),
		};
	}

	static deserialize(data: any): BigContainer {
		const c = new BigContainer();
		c.restoreCoreFields(data);
		if (data.loot) {
			c.loot = data.loot.map((l: any) => LootBox.deserialize(l));
		}
		return c;
	}
}
